import {faCheck} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Checkbox, ConfigProvider, Modal, Tooltip} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import {ReactElement, useMemo, useState} from 'react';

import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {LocalePlural} from '../../../../../../../../provider/locale/locale-plural';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {LangKeyType} from '../../../../../../../../provider/locale/translation/type';
import {useModal} from '../../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {useSendAbuseMutation} from '../../../../../../../../service/reviews/reviews-abuse-hook';
import {ReviewDataType} from '../../../../../../../../service/reviews/reviews-type';
import {Form} from '../../../../../../../../typings/antd';
import {getMessageFromReasonError} from '../../../../../../../../util/error';
import {isReviewAbuseAllowed} from '../../../../../reviews-helper';
import {ReviewDataWithCatalogType} from '../../../review-list-type';
import * as styles from '../../reply-form/new-abuse-modal/new-abuse-modal.scss';
import {abuseFormId, AbuseFormItemEnum, abuseReasons} from '../../reply-form/new-abuse-modal/new-abuse-modal-const';
import {processBatch} from '../../review-helper';

type AbuseFormType = {
    choices: Array<LangKeyType>;
    description?: string;
};

type PropsType = {
    onCancel: () => void;
    onSuccess?: () => void;
    refreshReview: (reviewId: number) => Promise<unknown>;
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    selectedReviews: Array<ReviewDataWithCatalogType>;
};

export function BulkAbuseModal(props: PropsType): ReactElement {
    const {onCancel, onSuccess, selectedReviews, refreshReview} = props;
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [form] = Form.useForm<AbuseFormType>();
    const {mutateAsync, isLoading} = useSendAbuseMutation({suppressSnackbars: true, suppressErrors: true});
    const {getLocalizedString} = useLocale();
    const {modal} = useModal();
    const {snackbar} = useSnackbar();

    const formId = `${abuseFormId}-bulk`;

    const abuseableReviews = useMemo(
        () => selectedReviews.filter((review) => isReviewAbuseAllowed(review)),
        [selectedReviews]
    );

    const canAbuseCount = abuseableReviews.length;
    const totalCount = selectedReviews.length;
    const canAbuseAll = canAbuseCount === totalCount;
    const canAbuseNone = canAbuseCount === 0;

    async function submitAbuse(formValues: AbuseFormType) {
        const errors: Array<unknown> = [];

        try {
            // Process abuse submissions in batches
            await processBatch(abuseableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                try {
                    await mutateAsync({
                        choices: formValues?.choices?.map((choice) => getLocalizedString(choice)) || [],
                        description: formValues.description || '',
                        reviewId: review.id,
                    });
                } catch (error) {
                    errors.push(error);
                }
            });

            // If there were errors, show them (only once for all reviews)
            if (errors.length > 0) {
                const firstError = errors[0];
                const errorMessage = getMessageFromReasonError(firstError);

                if (errorMessage) {
                    snackbar.error(errorMessage);
                } else {
                    snackbar.error(getLocalizedString('ERROR__SOMETHING_WENT_WRONG'));
                }

                console.error(`Errors during bulk abuse submission (${errors.length} errors):`, errors);
                return;
            }

            // Refresh all reviews to show updated data in batches
            await processBatch(abuseableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                await refreshReview(review.id);
            });

            snackbar.success({
                description: getLocalizedString('REVIEWS__COMPLAINT__COMPLAINT_HAS_BEEN_SENT__DESCRIPTION'),
                message: getLocalizedString('REVIEWS__COMPLAINT__COMPLAINT_HAS_BEEN_SENT'),
            });

            if (onSuccess) {
                onSuccess();
            } else {
                onCancel();
            }
        } catch (error) {
            snackbar.error(getLocalizedString('ERROR__SOMETHING_WENT_WRONG'));
            console.error(error);
        }
    }

    async function onSubmit(formValues: AbuseFormType) {
        if (!canAbuseAll) {
            modal.confirm({
                maskClosable: true,
                title: <Locale stringKey="REVIEWS__BULK_ABUSE__CONFIRM_TITLE" />,
                content: (
                    <div>
                        <LocalePlural
                            count={totalCount}
                            fewKey="REVIEWS__BULK_ABUSE__CONFIRM_CONTENT__FEW"
                            manyKey="REVIEWS__BULK_ABUSE__CONFIRM_CONTENT__MANY"
                            singularKey="REVIEWS__BULK_ABUSE__CONFIRM_CONTENT__SINGULAR"
                            valueMap={{
                                canAbuseCount: String(canAbuseCount),
                                totalCount: String(totalCount),
                            }}
                        />
                    </div>
                ),
                okText: <Locale stringKey="TEXT__APPLY" />,
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                onOk: async () => {
                    await submitAbuse(formValues);
                },
            });

            return;
        }

        await submitAbuse(formValues);
    }

    function handleOnChangeForm() {
        if (
            form.getFieldValue(AbuseFormItemEnum.Description) ||
            (form.getFieldValue(AbuseFormItemEnum.Choices) && form.getFieldValue(AbuseFormItemEnum.Choices).length > 0)
        ) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Tooltip
                    key="tooltip"
                    title={canAbuseNone ? <Locale stringKey="REVIEWS__BULK_ABUSE__DISABLED_TOOLTIP" /> : null}
                >
                    <Button
                        disabled={isDisabled || canAbuseNone}
                        form={formId}
                        htmlType="submit"
                        key="ok"
                        loading={isLoading}
                        type="primary"
                    >
                        <Locale stringKey="TEXT__APPLY" />
                    </Button>
                </Tooltip>,
            ]}
            onCancel={onCancel}
            open
            title={<Locale stringKey="REVIEWS__BULK_COMPLAINT__TITLE" />}
        >
            <Form<AbuseFormType> form={form} id={formId} onChange={handleOnChangeForm} onFinish={onSubmit}>
                <ConfigProvider wave={{disabled: true}}>
                    <Form.Item<AbuseFormType> name={AbuseFormItemEnum.Choices} valuePropName="checked">
                        <Checkbox.Group className={styles.NewAbuseModal_options}>
                            {abuseReasons.map(({id, title}) => (
                                <Checkbox className={styles.NewAbuseModal_option} key={id} value={title}>
                                    {getLocalizedString(title)}
                                    <FontAwesomeIcon className={styles.NewAbuseModal_optionIcon} icon={faCheck} />
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </Form.Item>
                </ConfigProvider>
                <Form.Item<AbuseFormType> name={AbuseFormItemEnum.Description}>
                    <TextArea
                        placeholder={getLocalizedString('REVIEWS__COMPLAINT_POPUP__ENTER_YOUR_COMPLAINT_HERE')}
                        rows={5}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
